import { Description, UploadFile } from '@mui/icons-material'
import { Loading } from 'components'
import { translate } from 'locales'
import { formatLocalCap } from 'utils/date'
import React, { useEffect, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { findInvoices } from '../services'
import { IFinancialInvoiceRow } from '../types'
import {
  buildOpenInvoiceRow,
  formatCurrency,
  getOpenInvoicePeriods,
} from '../utils'
import { invoiceStatusColor, invoiceStatusLabelKey } from './statusColors'
import * as S from './styles'
import UploadInvoiceModal from './UploadModal'

const PAGE = 1
const LIMIT = 12

interface InvoicesProps {
  locale: string
}

const Invoices = ({ locale }: InvoicesProps) => {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<IFinancialInvoiceRow[]>([])
  const [uploadTarget, setUploadTarget] = useState<IFinancialInvoiceRow | null>(
    null
  )

  const fetchInvoices = async () => {
    setLoading(true)

    try {
      const closedRows = await new Promise<IFinancialInvoiceRow[]>((resolve) =>
        findInvoices(
          PAGE,
          LIMIT,
          resolve as Dispatch<SetStateAction<IFinancialInvoiceRow[]>>
        )
      )

      const closedPeriods = new Set(closedRows.map((row) => row.periodMonth))
      const openRows = getOpenInvoicePeriods(
        closedPeriods,
        new Date().getFullYear()
      ).map(buildOpenInvoiceRow)

      setRows(
        [...closedRows, ...openRows].sort((a, b) =>
          b.periodMonth.localeCompare(a.periodMonth)
        )
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchInvoices()
  }, [])

  const handleUploadSuccess = () => {
    setUploadTarget(null)
    fetchInvoices()
  }

  return (
    <S.Section>
      {loading && <Loading title={translate('financial.loading')} />}

      <h2>{translate('financial.invoices.title')}</h2>
      <span className="section-subtitle">
        {translate('financial.invoices.subtitle')}
      </span>

      <S.List>
        {rows.map((row) => (
          <S.Row key={row.periodMonth}>
            <div className="invoice-info">
              <S.IconSquare available={row.status !== 'pending'}>
                <Description />
              </S.IconSquare>

              <div className="invoice-details">
                <span className="invoice-month">
                  {formatLocalCap(`${row.periodMonth}-01`, 'MMMM yyyy')}
                </span>
                <span className="invoice-subtitle">
                  {row.invoiceFileName ??
                    translate('financial.invoices.nfPending')}
                  {' · '}
                  {formatCurrency(row.totalValue, locale)}
                </span>
              </div>
            </div>

            <div className="invoice-actions">
              <S.StatusBadge color={invoiceStatusColor(row.status)}>
                <S.Dot color={invoiceStatusColor(row.status)} />
                {translate(invoiceStatusLabelKey(row.status))}
              </S.StatusBadge>

              <S.UploadButton
                type="button"
                disabled={row.status === 'pending'}
                title={
                  row.status === 'pending'
                    ? translate('financial.invoices.waitClosingHint')
                    : undefined
                }
                onClick={() => setUploadTarget(row)}
              >
                <UploadFile />
                {row.invoiceFileName
                  ? translate('financial.invoices.replaceButton')
                  : translate('financial.invoices.uploadButton')}
              </S.UploadButton>
            </div>
          </S.Row>
        ))}
      </S.List>

      {!loading && rows.length === 0 && (
        <S.EmptyState>{translate('financial.invoices.empty')}</S.EmptyState>
      )}

      {uploadTarget && uploadTarget.settlementId && (
        <UploadInvoiceModal
          settlementId={uploadTarget.settlementId}
          isReplace={Boolean(uploadTarget.invoiceFileName)}
          onClose={() => setUploadTarget(null)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </S.Section>
  )
}

export default Invoices
